/**
 * GameEngine - Core game logic for the Path Game
 * Manages game state, validates moves, and emits events
 */

import type {
  Point,
  Segment,
  GameState,
  Move,
  MoveRecord,
  GameSettings,
  GameEventType,
  GameEventData,
} from './types';

import {
  distance,
  pointsEqual,
  pointOnSegment,
  cloneState,
  isPointOccupiedInState,
  isValidSegmentInState,
  applyMoveToState,
  getValidMovesForState,
  canPlayerMoveInState,
} from './gameUtils';

// Re-export geometry functions for backwards compatibility
export { distance, pointOnSegment, segmentsIntersect } from './gameUtils';

export class GameEngine extends EventTarget {
  private state: GameState;
  private settings: GameSettings;
  private moveHistory: MoveRecord[] = [];

  constructor(settings: GameSettings) {
    super();
    this.settings = settings;
    this.state = this.createInitialState();
  }

  /** Create initial game state */
  private createInitialState(): GameState {
    return {
      currentPlayer: 1,
      players: {
        1: { pos: null, path: [], lastDist: 0 },
        2: { pos: null, path: [], lastDist: 0 },
      },
      allPoints: [],
      allSegments: [],
      gameOver: false,
      winner: null,
      gridSize: this.settings.gridSize,
    };
  }

  /** Get current game state (immutable copy) */
  getState(): GameState {
    return cloneState(this.state);
  }

  /** Set game state from external source (e.g., multiplayer server) */
  setState(newState: GameState): void {
    this.state = cloneState(newState);
    this.emit('stateChange', { state: this.getState() });
    
    if (this.state.gameOver && this.state.winner) {
      this.emit('gameOver', { winner: this.state.winner, state: this.getState() });
    }
  }

  /** Set move history from external source (e.g., multiplayer server) */
  setMoveHistory(moves: MoveRecord[]): void {
    this.moveHistory = moves.map(m => ({ ...m }));
  }

  /** Get game settings */
  getSettings(): GameSettings {
    return { ...this.settings };
  }

  /** Get move history */
  getMoveHistory(): MoveRecord[] {
    return [...this.moveHistory];
  }

  /** Reset the game to initial state */
  resetGame(): void {
    this.state = this.createInitialState();
    this.moveHistory = [];
    this.emit('stateChange', { state: this.getState() });
  }

  /** Check if a point is occupied */
  isPointOccupied(point: Point): boolean {
    return isPointOccupiedInState(this.state, point);
  }

  /** Check if a segment is valid */
  isValidSegment(segment: Segment, excludePoint: Point | null = null): boolean {
    return isValidSegmentInState(this.state, segment, excludePoint);
  }

  /** Validate a move and return error message if invalid, or null if valid */
  validateMove(move: Move, playerNum: 1 | 2): string | null {
    const player = this.state.players[playerNum];

    if (this.state.currentPlayer !== playerNum) {
      return 'Not your turn';
    }

    if (this.state.gameOver) {
      return 'Game is already over';
    }

    if (this.isPointOccupied(move.to)) {
      return 'Destination point is occupied';
    }

    if (move.isFirstMove) {
      if (this.isPointOccupied(move.from)) {
        return 'Starting point is occupied';
      }

      const segment: Segment = { from: move.from, to: move.to };
      if (!this.isValidSegment(segment)) {
        if (this.state.allPoints.some(p => pointOnSegment(p, segment))) {
          return 'Cannot pass through an existing point';
        }
        return 'Cannot cross existing paths';
      }
    } else {
      if (!player.pos) {
        return 'Player has not made first move yet';
      }

      if (!pointsEqual(move.from, player.pos)) {
        return 'Must move from current position';
      }

      const moveDist = distance(move.from, move.to);
      if (moveDist <= player.lastDist) {
        return `Move too short! Need > ${player.lastDist.toFixed(2)}, got ${moveDist.toFixed(2)}`;
      }

      const segment: Segment = { from: move.from, to: move.to };
      if (!this.isValidSegment(segment, player.pos)) {
        const passesThrough = this.state.allPoints.some(p => {
          if (pointsEqual(p, player.pos!)) return false;
          return pointOnSegment(p, segment);
        });
        return passesThrough ? 'Cannot pass through an existing point' : 'Cannot cross existing paths';
      }
    }

    return null;
  }

  /** Make a move - returns true if successful */
  makeMove(move: Move): boolean {
    const playerNum = this.state.currentPlayer;
    const error = this.validateMove(move, playerNum);

    if (error) {
      this.emit('invalidMove', { reason: error, move });
      return false;
    }

    this.applyMove(move, playerNum);
    return true;
  }

  /** Apply a move to the game state (assumes move is valid) */
  private applyMove(move: Move, playerNum: 1 | 2): void {
    const moveDist = distance(move.from, move.to);

    // Store move in history
    this.moveHistory.push({
      player: playerNum,
      from: { ...move.from },
      to: { ...move.to },
      isFirstMove: move.isFirstMove,
      dist: moveDist,
    });

    // Apply move using shared utility
    this.state = applyMoveToState(this.state, move, playerNum);

    this.emit('moveExecuted', { move, player: playerNum, state: this.getState() });

    // Check if next player can move
    if (!this.canPlayerMove(this.state.currentPlayer)) {
      this.state.gameOver = true;
      this.state.winner = playerNum;
      this.emit('gameOver', { winner: playerNum, state: this.getState() });
    } else {
      this.emit('stateChange', { state: this.getState() });
    }
  }

  /** Check if a player can make any valid move */
  canPlayerMove(playerNum: 1 | 2): boolean {
    return canPlayerMoveInState(this.state, playerNum);
  }

  /** Get all valid moves for a player */
  getValidMoves(playerNum: 1 | 2): Move[] {
    return getValidMovesForState(this.state, playerNum);
  }

  /** Clone the game state */
  cloneState(state: GameState): GameState {
    return cloneState(state);
  }

  /** Create a simulated state after applying a move (for AI lookahead) */
  simulateMove(state: GameState, move: Move, playerNum: 1 | 2): GameState {
    return applyMoveToState(state, move, playerNum);
  }

  /** Emit a typed game event */
  private emit<T extends GameEventType>(type: T, detail: GameEventData[T]): void {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  /** Add event listener with proper typing */
  on<T extends GameEventType>(
    type: T,
    listener: (event: CustomEvent<GameEventData[T]>) => void
  ): void {
    this.addEventListener(type, listener as EventListener);
  }

  /** Remove event listener */
  off<T extends GameEventType>(
    type: T,
    listener: (event: CustomEvent<GameEventData[T]>) => void
  ): void {
    this.removeEventListener(type, listener as EventListener);
  }
}
